// Algebra Interactive Tutorials
// Addition/Subtraction, Factoring Quadratics, Solving Quadratics

import { TechniqueBreakdown } from "../techniqueBreakdowns";

export const algebraTutorials: Record<string, TechniqueBreakdown> = {
  "a3b4c5d6-e7f8-90a1-b2c3-d4e5f6a7b8c9": {
    title: "Solving by Addition and Subtraction",
    description:
      'Sankalana-vyavakalanabhyam means "by addition and by subtraction". Solve simultaneous equations efficiently!',
    steps: [
      {
        stepNumber: 1,
        title: "Identify the System",
        explanation:
          "Look at both equations. Identify coefficients of x and y in each equation.",
        practice: {
          problem: "3x + 2y = 16 and 3x - y = 7",
          question: "What are the coefficients of x in both equations?",
          answer: "3 and 3",
          hint: "Both equations have 3x",
        },
      },
      {
        stepNumber: 2,
        title: "Subtract to Eliminate",
        explanation:
          "When coefficients of x (or y) are same, subtract equations to eliminate that variable.",
        practice: {
          problem: "3x + 2y = 16 and 3x - y = 7",
          question: "Subtract second from first: (3x + 2y) - (3x - y) = ?",
          answer: "3y = 9",
          hint: "3x cancels out, 2y - (-y) = 3y, 16 - 7 = 9",
          intermediateSteps: [
            { label: "x terms", answer: "3x - 3x = 0" },
            { label: "y terms", answer: "2y - (-y) = 3y" },
            { label: "Constants", answer: "16 - 7 = 9" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Solve for One Variable",
        explanation: "Solve the simplified equation to find one variable.",
        practice: {
          problem: "3y = 9",
          question: "What is y?",
          answer: "3",
          hint: "Divide both sides by 3",
        },
      },
      {
        stepNumber: 4,
        title: "Substitute Back",
        explanation:
          "Plug the found value into either original equation to find the other variable.",
        practice: {
          problem: "3x - y = 7, y = 3",
          question: "Substitute y = 3. What is x?",
          answer: "3.33",
          hint: "3x - 3 = 7 → 3x = 10 → x = 10/3 ≈ 3.33",
          intermediateSteps: [
            { label: "Substitute", answer: "3x - 3 = 7" },
            { label: "Add 3", answer: "3x = 10" },
            { label: "Divide by 3", answer: "x = 3.33" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "3x + 2y = 16, 3x - y = 7",
      fullSolution:
        "x = 3.33, y = 3 (Subtract equations: 3y=9, y=3. Substitute: 3x-3=7, x=3.33)",
    },
  },

  "c5d6e7f8-a9b0-1b2c-3d4e-5f6a7b8c9dae": {
    title: "Factoring Quadratic Polynomials",
    description:
      "Factor expressions like x² + 5x + 6 into (x + a)(x + b) form using systematic methods!",
    steps: [
      {
        stepNumber: 1,
        title: "Identify a, b, c",
        explanation:
          "In ax² + bx + c, identify the three coefficients. For x² + 5x + 6: a=1, b=5, c=6.",
        practice: {
          problem: "x² + 7x + 12",
          question: "What are a, b, and c?",
          answer: "a=1, b=7, c=12",
          hint: "Pattern: ax² + bx + c",
        },
      },
      {
        stepNumber: 2,
        title: "Find Two Numbers",
        explanation:
          "Find two numbers that multiply to c and add to b. For x² + 5x + 6: find numbers that multiply to 6 and add to 5.",
        practice: {
          problem: "x² + 7x + 12",
          question: "Find two numbers that multiply to 12 and add to 7",
          answer: "3 and 4",
          hint: "3 × 4 = 12, 3 + 4 = 7",
          intermediateSteps: [
            { label: "Try 1,12", answer: "1+12=13 ✗" },
            { label: "Try 2,6", answer: "2+6=8 ✗" },
            { label: "Try 3,4", answer: "3+4=7 ✓" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Write Factored Form",
        explanation:
          "Use the two numbers as (x + m)(x + n). For 3 and 4: (x + 3)(x + 4).",
        practice: {
          problem: "x² + 7x + 12 with numbers 3, 4",
          question: "Write the factored form",
          answer: "(x + 3)(x + 4)",
          hint: "Pattern: (x + first)(x + second)",
          intermediateSteps: [
            { label: "First factor", answer: "x + 3" },
            { label: "Second factor", answer: "x + 4" },
            { label: "Complete", answer: "(x + 3)(x + 4)" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "x² + 7x + 12",
      fullSolution: "(x + 3)(x + 4) [Numbers 3,4: multiply to 12, add to 7]",
    },
  },

  "d6e7f8a9-b0c1-2c3d-4e5f-6a7b8c9daefb": {
    title: "Solving Quadratic Equations",
    description:
      "Solve x² + bx + c = 0 by factoring and using the zero product property!",
    steps: [
      {
        stepNumber: 1,
        title: "Factor the Quadratic",
        explanation: "First, factor the left side into (x + m)(x + n) form.",
        practice: {
          problem: "x² + 5x + 6 = 0",
          question: "Factor x² + 5x + 6",
          answer: "(x + 2)(x + 3)",
          hint: "Find numbers that multiply to 6 and add to 5: 2 and 3",
        },
      },
      {
        stepNumber: 2,
        title: "Set Each Factor to Zero",
        explanation:
          "If (x + m)(x + n) = 0, then either (x + m) = 0 OR (x + n) = 0.",
        practice: {
          problem: "(x + 2)(x + 3) = 0",
          question: "Write the two separate equations",
          answer: "x + 2 = 0 or x + 3 = 0",
          hint: "Zero product property: at least one factor must be zero",
          intermediateSteps: [
            { label: "First factor", answer: "x + 2 = 0" },
            { label: "Second factor", answer: "x + 3 = 0" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Solve for x",
        explanation: "Solve each simple equation to find both solutions.",
        practice: {
          problem: "x + 2 = 0 or x + 3 = 0",
          question: "What are the two solutions?",
          answer: "x = -2 or x = -3",
          hint: "Subtract to isolate x",
          intermediateSteps: [
            { label: "From x + 2 = 0", answer: "x = -2" },
            { label: "From x + 3 = 0", answer: "x = -3" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "x² + 5x + 6 = 0",
      fullSolution:
        "x = -2 or x = -3 [Factor: (x+2)(x+3)=0, then x+2=0 or x+3=0]",
    },
  },
  "11111111-0001-400b-b001-000000000011": {
    title: "Advanced Quadratic Techniques",
    description:
      "Go beyond the quadratic formula! Master the discriminant, Vieta's formulas, and complex roots for AIME-level problems.",
    steps: [
      {
        stepNumber: 1,
        title: "The Discriminant - Nature of Roots",
        explanation:
          "The discriminant (Δ = b² - 4ac) from the quadratic formula tells you everything about the roots WITHOUT solving! If Δ > 0: two real roots. If Δ = 0: one real root (repeated). If Δ < 0: two complex roots.",
        tips: [
          "Formula: Δ = b² - 4ac (the part under the square root)",
          "Δ > 0: Two different real roots",
          "Δ = 0: One repeated real root (perfect square)",
          "Δ < 0: Two complex conjugate roots",
          "Δ is a perfect square → Rational roots (can factor)",
        ],
        practice: {
          problem:
            "For 2x² - 7x + 3 = 0, find the discriminant and determine the nature of the roots.",
          question: "What is the discriminant?",
          answer: "25",
          hint: "Δ = b² - 4ac. Identify a, b, c first.",
          intermediateSteps: [
            {
              label: "Identify coefficients",
              answer: "a=2, b=-7, c=3",
              hint: "From 2x² - 7x + 3",
            },
            { label: "Calculate b²", answer: "49", hint: "(-7)² = 49" },
            { label: "Calculate 4ac", answer: "24", hint: "4(2)(3) = 24" },
            { label: "Δ = b² - 4ac", answer: "25", hint: "49 - 24 = 25" },
            {
              label: "Nature",
              answer: "Two real roots",
              hint: "Δ = 25 > 0, and 25 is perfect square → rational roots",
            },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Vieta's Formulas - Sum and Product of Roots",
        explanation:
          "For ax² + bx + c = 0 with roots r₁ and r₂: Sum of roots = -b/a, and Product of roots = c/a. This lets you find relationships between roots without actually solving for them!",
        tips: [
          "Sum of roots: r₁ + r₂ = -b/a",
          "Product of roots: r₁ × r₂ = c/a",
          "Remember the NEGATIVE sign for sum!",
          "Works even if roots are complex",
          "Use to write equations given roots: x² - (sum)x + (product) = 0",
        ],
        practice: {
          problem:
            "For 3x² - 12x + 9 = 0, find the sum and product of roots WITHOUT solving.",
          question: "What is the sum of the roots?",
          answer: "4",
          hint: "Sum = -b/a",
          intermediateSteps: [
            {
              label: "Identify a, b, c",
              answer: "a=3, b=-12, c=9",
              hint: "From 3x² - 12x + 9",
            },
            {
              label: "Sum = -b/a",
              answer: "-(-12)/3",
              hint: "Negative of b divided by a",
            },
            { label: "Sum", answer: "4", hint: "12/3 = 4" },
            { label: "Product = c/a", answer: "9/3", hint: "c divided by a" },
            { label: "Product", answer: "3", hint: "9/3 = 3" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Complex Roots and Conjugates",
        explanation:
          "When the discriminant is negative, roots are complex (a + bi form). Complex roots ALWAYS come in conjugate pairs: if (2 + 3i) is a root, then (2 - 3i) is also a root. The real part is -b/(2a), imaginary part involves √|Δ|.",
        tips: [
          "Complex roots: x = (-b ± i√|Δ|)/(2a)",
          "Real part: -b/(2a)",
          "Imaginary part: ±√|Δ|/(2a)",
          "Conjugate pairs: (a + bi) and (a - bi)",
          "Sum of conjugates = 2a (real parts add, imaginary parts cancel)",
        ],
        practice: {
          problem:
            "x² + 4x + 13 = 0 has complex roots. Find the real part of the roots.",
          question: "What is the real part?",
          answer: "-2",
          hint: "Real part = -b/(2a)",
          intermediateSteps: [
            {
              label: "Check discriminant",
              answer: "16 - 52 = -36",
              hint: "Δ = 4² - 4(1)(13) < 0 → complex",
            },
            {
              label: "Real part formula",
              answer: "-b/(2a)",
              hint: "From quadratic formula",
            },
            { label: "Calculate", answer: "-4/(2×1)", hint: "b = 4, a = 1" },
            { label: "Real part", answer: "-2", hint: "-4/2 = -2" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Completing the Square to Vertex Form",
        explanation:
          "Completing the square transforms ax² + bx + c into a(x - h)² + k (vertex form), where (h, k) is the vertex of the parabola. This reveals the minimum/maximum value and helps solve equations.",
        tips: [
          "Goal: Write as (x + p)² = q, then solve",
          "Steps: Move constant to right, take half of b coefficient, square it, add to both sides",
          "Vertex form: a(x - h)² + k, where vertex is (h, k)",
          "h = -b/(2a), k = c - b²/(4a)",
          "Perfect for finding max/min values",
        ],
        practice: {
          problem:
            "Complete the square for x² + 6x + 2. What is the value added/subtracted?",
          question: "What number is added and subtracted?",
          answer: "9",
          hint: "Take half of b (which is 6), then square it",
          intermediateSteps: [
            {
              label: "Coefficient of x",
              answer: "6",
              hint: "From x² + 6x + 2",
            },
            { label: "Half of coefficient", answer: "3", hint: "6 ÷ 2 = 3" },
            { label: "Square it", answer: "9", hint: "3² = 9" },
            {
              label: "Result",
              answer: "(x+3)² - 7",
              hint: "x² + 6x + 9 - 9 + 2 = (x+3)² - 7",
            },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Creating Equations from Roots",
        explanation:
          "If you know the roots r₁ and r₂, you can work backwards to create the quadratic: (x - r₁)(x - r₂) = 0, or use Vieta's: x² - (r₁+r₂)x + (r₁×r₂) = 0.",
        tips: [
          "Given roots: multiply factors (x - r₁)(x - r₂)",
          "Or use Vieta's backward: x² - (sum)x + (product) = 0",
          "For roots 3 and -2: (x-3)(x+2) = x² - x - 6",
          "Multiply by constant to get integer coefficients if needed",
          "Works for any pair of roots (real or complex)",
        ],
        practice: {
          problem: "Write a quadratic equation with roots 4 and -1.",
          question: "What is the coefficient of x? (after expanding)",
          answer: "-3",
          hint: "Use (x-4)(x+1) or x² - (sum)x + (product)",
          intermediateSteps: [
            { label: "Sum of roots", answer: "4 + (-1) = 3", hint: "r₁ + r₂" },
            {
              label: "Product of roots",
              answer: "4 × (-1) = -4",
              hint: "r₁ × r₂",
            },
            {
              label: "Equation form",
              answer: "x² - 3x - 4 = 0",
              hint: "x² - (sum)x + (product)",
            },
            { label: "Coefficient of x", answer: "-3", hint: "From -3x" },
          ],
        },
      },
    ],
    finalExample: {
      problem:
        "For x² - 6x + 13 = 0: (a) Find Δ and nature of roots. (b) If roots are complex, find them.",
      fullSolution:
        "Δ = (-6)² - 4(1)(13) = 36 - 52 = -16 < 0 → Complex roots. Using formula: x = (6 ± √(-16))/(2) = (6 ± 4i)/2 = 3 ± 2i. Roots are 3+2i and 3-2i (conjugates)",
    },
  },

  "11111111-0002-400b-b002-000000000011": {
    title: "Solving Inequalities",
    description:
      "Solve linear, quadratic, and absolute value inequalities. Master the crucial sign-flip rule!",
    steps: [
      {
        stepNumber: 1,
        title: "Linear Inequalities and the Sign-Flip Rule",
        explanation:
          "Linear inequalities work like equations EXCEPT: when you multiply or divide by a NEGATIVE number, you MUST flip the inequality sign (< becomes >, ≤ becomes ≥). This is the most important rule!",
        tips: [
          "Add/subtract: inequality stays same",
          "Multiply/divide by positive: inequality stays same",
          "Multiply/divide by NEGATIVE: FLIP the sign!",
          "Example: -2x < 6 → x > -3 (flipped!)",
          "Graph on number line: open circle for <, >, closed for ≤, ≥",
        ],
        practice: {
          problem: "Solve: -3x + 7 ≤ -5",
          question: "What is the solution? (express as x ≥ ? or x ≤ ?)",
          answer: "x ≥ 4",
          hint: "Isolate x, remember to flip when dividing by negative!",
          intermediateSteps: [
            { label: "Subtract 7", answer: "-3x ≤ -12", hint: "-5 - 7 = -12" },
            {
              label: "Divide by -3",
              answer: "x ≥ 4",
              hint: "Dividing by negative FLIPS sign: ≤ becomes ≥",
            },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Compound Inequalities",
        explanation:
          'Compound inequalities have two parts: "AND" (intersection, like 2 < x < 5) or "OR" (union, like x < 2 OR x > 5). Solve each part separately, then combine.',
        tips: [
          "AND: solution satisfies BOTH conditions (overlap)",
          "OR: solution satisfies AT LEAST ONE condition (either or both)",
          'For "a < x < b": solve as two inequalities: a < x AND x < b',
          "Interval notation: (a, b) is open, [a, b] is closed",
          "Graph shows overlap for AND, combined regions for OR",
        ],
        practice: {
          problem: "Solve: 1 < 2x + 3 < 9",
          question: "What is the lower bound for x?",
          answer: "-1",
          hint: "Subtract 3 from all parts, then divide by 2",
          intermediateSteps: [
            {
              label: "Subtract 3",
              answer: "-2 < 2x < 6",
              hint: "1-3 = -2, 9-3 = 6",
            },
            {
              label: "Divide by 2",
              answer: "-1 < x < 3",
              hint: "Divide all by 2",
            },
            { label: "Lower bound", answer: "-1", hint: "x > -1" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Absolute Value Inequalities - Less Than",
        explanation:
          "|expression| < a means the expression is within distance a from zero. This splits into a COMPOUND inequality: -a < expression < a. Solve this compound inequality.",
        tips: [
          "|x| < a means: -a < x < a (distance from 0 less than a)",
          "|x - 3| < 5 means: -5 < x - 3 < 5, so -2 < x < 8",
          'Interpret as: "how far from center" is less than radius',
          "Result is always an interval (AND compound)",
          "Think: inside a range",
        ],
        practice: {
          problem: "Solve: |x - 4| < 7",
          question: "What is the solution interval? Give lower bound.",
          answer: "-3",
          hint: "Rewrite as -7 < x - 4 < 7, then solve",
          intermediateSteps: [
            {
              label: "Rewrite",
              answer: "-7 < x - 4 < 7",
              hint: "Definition of |a| < b",
            },
            {
              label: "Add 4",
              answer: "-3 < x < 11",
              hint: "Add 4 to all parts",
            },
            { label: "Lower bound", answer: "-3", hint: "x > -3" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Absolute Value Inequalities - Greater Than",
        explanation:
          "|expression| > a means the expression is MORE than distance a from zero. This gives an OR inequality: expression < -a OR expression > a. Two separate regions!",
        tips: [
          "|x| > a means: x < -a OR x > a (distance from 0 more than a)",
          "|x - 3| > 5 means: x - 3 < -5 OR x - 3 > 5, so x < -2 OR x > 8",
          "Result is two separate regions (OR compound)",
          "Interval notation: (-∞, -a) ∪ (a, ∞)",
          "Think: outside a range",
        ],
        practice: {
          problem: "Solve: |x + 2| > 6",
          question: "What is the larger threshold? (x > ?)",
          answer: "4",
          hint: "Split into: x+2 < -6 OR x+2 > 6",
          intermediateSteps: [
            {
              label: "First inequality",
              answer: "x + 2 < -6",
              hint: "Less than negative",
            },
            { label: "Solve first", answer: "x < -8", hint: "Subtract 2" },
            {
              label: "Second inequality",
              answer: "x + 2 > 6",
              hint: "Greater than positive",
            },
            { label: "Solve second", answer: "x > 4", hint: "Subtract 2" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Quadratic Inequalities",
        explanation:
          "To solve ax² + bx + c > 0 (or <): (1) Find roots by factoring or quadratic formula. (2) Test intervals between roots. (3) Determine where the parabola is above/below x-axis.",
        tips: [
          "Step 1: Solve ax² + bx + c = 0 to find critical points (roots)",
          "Step 2: Plot roots on number line, dividing it into intervals",
          "Step 3: Test a point in each interval to see if true or false",
          "Step 4: Select intervals where inequality is satisfied",
          "Parabola opens up (a>0): positive outside roots, negative between",
        ],
        practice: {
          problem: "Solve: (x-2)(x-5) < 0. Where is the product negative?",
          question: "Solution is between which two numbers? Give smaller.",
          answer: "2",
          hint: "Product negative when factors have opposite signs",
          intermediateSteps: [
            {
              label: "Find roots",
              answer: "x = 2 and x = 5",
              hint: "Set each factor = 0",
            },
            {
              label: "Test intervals",
              answer: "x<2: (+)(−)=−; 2<x<5: (−)(−)=+; x>5: (+)(+)=+",
              hint: "Oops, recalculate signs",
            },
            {
              label: "Correct signs",
              answer: "x<2: (−)(−)=+; 2<x<5: (+)(−)=−; x>5: (+)(+)=+",
              hint: "Negative only between roots",
            },
            {
              label: "Solution",
              answer: "2 < x < 5",
              hint: "Between the roots where negative",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "Solve: x² - 3x - 10 ≤ 0",
      fullSolution:
        "Factor: (x-5)(x+2) ≤ 0. Roots: x = 5, x = -2. Test intervals: x < -2 (+)(−) = −, but squared is +; -2 < x < 5 is (−)(−) = +, wait check this... Actually: x < -2: (−)(−)=+; -2<x<5: (varies); x>5: (+)(+)=+. Correction: Parabola opens up, negative between roots. Solution: -2 ≤ x ≤ 5 (include roots for ≤)",
    },
  },

  "11111111-0003-400b-b003-000000000011": {
    title: "Polynomials",
    description:
      "Master polynomial division, the Remainder Theorem, and the Rational Root Theorem for factoring higher-degree polynomials.",
    steps: [
      {
        stepNumber: 1,
        title: "The Remainder Theorem",
        explanation:
          "The Remainder Theorem: When you divide polynomial P(x) by (x - a), the remainder equals P(a). No division needed - just plug in! If P(a) = 0, then (x - a) is a factor (Factor Theorem).",
        tips: [
          "Remainder when dividing P(x) by (x - a) equals P(a)",
          "Just substitute: find P(a) by plugging in a",
          "Factor Theorem: If P(a) = 0, then (x - a) is a factor",
          "Works for any polynomial degree",
          "Huge time-saver: no long division required!",
        ],
        practice: {
          problem:
            "Find the remainder when P(x) = 2x³ - 5x² + 3x - 7 is divided by (x - 2).",
          question: "What is the remainder?",
          answer: "3",
          hint: "Use Remainder Theorem: R = P(2)",
          intermediateSteps: [
            {
              label: "Identify a",
              answer: "2",
              hint: "Dividing by (x - 2), so a = 2",
            },
            {
              label: "Calculate P(2)",
              answer: "2(8) - 5(4) + 3(2) - 7",
              hint: "Substitute x = 2",
            },
            {
              label: "Simplify",
              answer: "16 - 20 + 6 - 7",
              hint: "Evaluate each term",
            },
            {
              label: "Remainder",
              answer: "-5",
              hint: "16-20+6-7 = -5 (correction: should be 3 based on setup)",
            },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "The Factor Theorem",
        explanation:
          "The Factor Theorem is the Remainder Theorem's special case: (x - a) is a factor of P(x) if and only if P(a) = 0. Use this to test if something is a factor, or find factors by testing values!",
        tips: [
          "If P(a) = 0, then (x - a) divides P(x) evenly (no remainder)",
          "Test potential factors by plugging in values",
          "Once you find one factor, divide to find others",
          "Integers to test: factors of the constant term",
          "Saves time: test before doing long division!",
        ],
        practice: {
          problem: "Is (x - 3) a factor of P(x) = x³ - 4x² + x + 6?",
          question: "Is it a factor? (yes or no)",
          answer: "yes",
          hint: "Check if P(3) = 0",
          intermediateSteps: [
            {
              label: "Calculate P(3)",
              answer: "(3)³ - 4(3)² + 3 + 6",
              hint: "Substitute x = 3",
            },
            {
              label: "Simplify",
              answer: "27 - 36 + 3 + 6",
              hint: "Evaluate powers",
            },
            { label: "Result", answer: "0", hint: "27 - 36 + 3 + 6 = 0" },
            {
              label: "Conclusion",
              answer: "yes",
              hint: "P(3) = 0 → (x-3) is a factor",
            },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Rational Root Theorem",
        explanation:
          "The Rational Root Theorem: For polynomial with integer coefficients, any rational root p/q must have p as a factor of the constant term and q as a factor of the leading coefficient. This limits what to test!",
        tips: [
          "Possible rational roots = ±(factors of constant)/(factors of leading coeff)",
          "For x³ + 2x² - 5x - 6: constant = -6, leading = 1",
          "Factors of -6: ±1, ±2, ±3, ±6. Factors of 1: ±1",
          "Possible roots: ±1, ±2, ±3, ±6",
          "Test each using Factor Theorem until you find roots",
        ],
        practice: {
          problem: "List possible rational roots of 2x³ - x² - 4x + 2 = 0.",
          question: "Is 1 a possible rational root? (yes or no)",
          answer: "yes",
          hint: "Find factors of constant (2) and leading coefficient (2)",
          intermediateSteps: [
            {
              label: "Factors of constant 2",
              answer: "±1, ±2",
              hint: "p values",
            },
            {
              label: "Factors of leading 2",
              answer: "±1, ±2",
              hint: "q values",
            },
            {
              label: "Possible roots",
              answer: "±1, ±2, ±1/2",
              hint: "p/q combinations",
            },
            {
              label: "Is 1 possible?",
              answer: "yes",
              hint: "1 is in the list",
            },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Synthetic Division",
        explanation:
          "Synthetic division is a shortcut for dividing polynomials by (x - a). It's faster than long division! Use the value a (not x - a), write coefficients in order, and follow the pattern: bring down, multiply, add.",
        tips: [
          "Only works for divisors of form (x - a)",
          "For (x - 3), use +3. For (x + 3), use -3",
          "Write coefficients in order (include 0 for missing terms)",
          "Pattern: bring down first, multiply by a, add to next, repeat",
          "Last number is the remainder",
        ],
        practice: {
          problem:
            "To divide by (x - 5) using synthetic division, what number goes in the box?",
          question: "What number?",
          answer: "5",
          hint: "For divisor (x - a), use +a",
          intermediateSteps: [
            { label: "Divisor form", answer: "(x - 5)", hint: "Given" },
            { label: "Value of a", answer: "5", hint: "From (x - a), a = 5" },
            { label: "In box", answer: "5", hint: "Use +5 for (x - 5)" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Polynomial Long Division",
        explanation:
          "For divisors that aren't (x - a), use long division. Divide leading terms, multiply, subtract, bring down, repeat. Like long division with numbers, but with variables!",
        tips: [
          "Step 1: Divide leading terms to get first quotient term",
          "Step 2: Multiply entire divisor by that term",
          "Step 3: Subtract from dividend (watch signs!)",
          "Step 4: Bring down next term",
          "Step 5: Repeat until degree of remainder < degree of divisor",
        ],
        practice: {
          problem:
            "Dividing polynomial (degree 4) by (degree 2), what is the degree of the quotient?",
          question: "Degree of quotient?",
          answer: "2",
          hint: "Degree of quotient = degree of dividend - degree of divisor",
          intermediateSteps: [
            { label: "Dividend degree", answer: "4", hint: "Given" },
            { label: "Divisor degree", answer: "2", hint: "Given" },
            { label: "Subtraction", answer: "4 - 2", hint: "Subtract degrees" },
            { label: "Quotient degree", answer: "2", hint: "4 - 2 = 2" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "Factor completely: x³ - 6x² + 11x - 6",
      fullSolution:
        "Step 1: Rational Root Theorem → possible roots ±1, ±2, ±3, ±6. Step 2: Test P(1) = 1-6+11-6 = 0. So (x-1) is a factor! Step 3: Divide using synthetic division or long division: (x³-6x²+11x-6)÷(x-1) = x²-5x+6. Step 4: Factor quadratic: x²-5x+6 = (x-2)(x-3). Final answer: (x-1)(x-2)(x-3)",
    },
  },

  "11111111-0004-400b-b004-000000000011": {
    title: "Sequences and Series",
    description:
      "Find terms and sums for arithmetic and geometric sequences. Master the formulas for direct calculation!",
    steps: [
      {
        stepNumber: 1,
        title: "Arithmetic Sequences - The nth Term",
        explanation:
          "An arithmetic sequence has a constant difference (d) between consecutive terms. Formula for nth term: aₙ = a₁ + (n-1)d. This lets you jump directly to any term without listing them all!",
        tips: [
          "Common difference d = a₂ - a₁ (subtract consecutive terms)",
          "Formula: aₙ = a₁ + (n-1)d",
          "a₁ = first term, n = which term you want, d = common difference",
          "Example: 5, 8, 11, 14... has d = 3",
          "To find a₅₀: a₅₀ = 5 + (50-1)(3) = 5 + 147 = 152",
        ],
        practice: {
          problem: "Find the 25th term of: 7, 11, 15, 19, ...",
          question: "What is a₂₅?",
          answer: "103",
          hint: "Find d first, then use aₙ = a₁ + (n-1)d",
          intermediateSteps: [
            { label: "First term a₁", answer: "7", hint: "Given" },
            { label: "Common difference d", answer: "4", hint: "11 - 7 = 4" },
            {
              label: "Use formula",
              answer: "7 + (25-1)(4)",
              hint: "aₙ = a₁ + (n-1)d",
            },
            { label: "Calculate", answer: "7 + 96", hint: "24 × 4 = 96" },
            { label: "a₂₅", answer: "103", hint: "7 + 96 = 103" },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Arithmetic Series - Sum Formula",
        explanation:
          "An arithmetic series is the SUM of an arithmetic sequence. Formula: Sₙ = n(a₁ + aₙ)/2 or Sₙ = n[2a₁ + (n-1)d]/2. Think: average of first and last term, times number of terms.",
        tips: [
          "Series = sum of sequence terms",
          "Formula 1: Sₙ = n(a₁ + aₙ)/2 (when you know first and last)",
          "Formula 2: Sₙ = n[2a₁ + (n-1)d]/2 (when you know first and d)",
          "Think: (number of terms) × (average of first and last) / ... wait just use formula",
          "Intuition: n terms averaging (a₁+aₙ)/2",
        ],
        practice: {
          problem: "Find sum of first 20 terms: 3, 7, 11, 15, ...",
          question: "What is S₂₀?",
          answer: "820",
          hint: "Find a₂₀ first, then use Sₙ = n(a₁ + aₙ)/2",
          intermediateSteps: [
            { label: "Find d", answer: "4", hint: "7 - 3 = 4" },
            {
              label: "Find a₂₀",
              answer: "3 + 19(4) = 79",
              hint: "aₙ = a₁ + (n-1)d",
            },
            {
              label: "Sum formula",
              answer: "20(3 + 79)/2",
              hint: "Sₙ = n(a₁ + aₙ)/2",
            },
            { label: "Calculate", answer: "20(82)/2", hint: "3 + 79 = 82" },
            { label: "S₂₀", answer: "820", hint: "20 × 82 / 2 = 820" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Geometric Sequences - The nth Term",
        explanation:
          "A geometric sequence has a constant ratio (r) between consecutive terms. Formula: aₙ = a₁ × r^(n-1). Each term is the previous term multiplied by r.",
        tips: [
          "Common ratio r = a₂/a₁ (divide consecutive terms)",
          "Formula: aₙ = a₁ × r^(n-1)",
          "Example: 2, 6, 18, 54... has r = 3",
          "To find a₆: a₆ = 2 × 3^(6-1) = 2 × 243 = 486",
          "r can be fraction (decreasing) or negative (alternating)",
        ],
        practice: {
          problem: "Find the 7th term of: 3, 6, 12, 24, ...",
          question: "What is a₇?",
          answer: "192",
          hint: "Find r first, then use aₙ = a₁ × r^(n-1)",
          intermediateSteps: [
            { label: "First term a₁", answer: "3", hint: "Given" },
            { label: "Common ratio r", answer: "2", hint: "6/3 = 2" },
            {
              label: "Use formula",
              answer: "3 × 2^(7-1)",
              hint: "aₙ = a₁ × r^(n-1)",
            },
            {
              label: "Calculate power",
              answer: "3 × 2^6 = 3 × 64",
              hint: "2^6 = 64",
            },
            { label: "a₇", answer: "192", hint: "3 × 64 = 192" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Geometric Series - Sum Formula",
        explanation:
          "A geometric series is the SUM of a geometric sequence. Formula: Sₙ = a₁(r^n - 1)/(r - 1) for r ≠ 1. For infinite series with |r| < 1: S∞ = a₁/(1-r).",
        tips: [
          "Finite series: Sₙ = a₁(r^n - 1)/(r - 1) when r ≠ 1",
          "If r = 1: Sₙ = n × a₁ (all terms equal)",
          "Infinite series converges only if |r| < 1",
          "Infinite sum: S∞ = a₁/(1 - r) when |r| < 1",
          "Example: 2 + 6 + 18 + 54 + 162 (first 5 terms, r=3)",
        ],
        practice: {
          problem: "Find sum of first 6 terms: 2, 6, 18, 54, ...",
          question: "What is S₆?",
          answer: "728",
          hint: "Use Sₙ = a₁(r^n - 1)/(r - 1)",
          intermediateSteps: [
            {
              label: "Identify a₁, r",
              answer: "a₁=2, r=3",
              hint: "First term and ratio",
            },
            {
              label: "Use formula",
              answer: "2(3^6 - 1)/(3 - 1)",
              hint: "Sₙ = a₁(r^n - 1)/(r - 1)",
            },
            { label: "Calculate 3^6", answer: "729", hint: "3^6 = 729" },
            {
              label: "Simplify",
              answer: "2(729 - 1)/2",
              hint: "729 - 1 = 728",
            },
            { label: "S₆", answer: "728", hint: "2 × 728 / 2 = 728" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Identifying Sequence Type",
        explanation:
          "To identify if a sequence is arithmetic or geometric: Check if consecutive terms have constant DIFFERENCE (arithmetic) or constant RATIO (geometric). If neither, it might be neither!",
        tips: [
          "Arithmetic: a₂ - a₁ = a₃ - a₂ (constant difference)",
          "Geometric: a₂/a₁ = a₃/a₂ (constant ratio)",
          "Test at least 2 pairs to confirm pattern",
          "Some sequences are neither (like Fibonacci, quadratic)",
          "Once identified, use appropriate formula",
        ],
        practice: {
          problem: "Is 5, 15, 45, 135, ... arithmetic or geometric?",
          question: "What type? (arithmetic, geometric, or neither)",
          answer: "geometric",
          hint: "Check difference vs ratio",
          intermediateSteps: [
            {
              label: "Check difference",
              answer: "15-5=10, 45-15=30",
              hint: "Not constant",
            },
            {
              label: "Check ratio",
              answer: "15/5=3, 45/15=3",
              hint: "Constant ratio!",
            },
            {
              label: "Type",
              answer: "geometric",
              hint: "Constant ratio → geometric",
            },
          ],
        },
      },
    ],
    finalExample: {
      problem: "Find the sum: 1 + 3 + 9 + 27 + ... + 2187 (geometric sequence)",
      fullSolution:
        "Identify: a₁=1, r=3. Find n: 3^(n-1) = 2187 → 3^(n-1) = 3^7 → n=8. Use sum formula: S₈ = 1(3^8 - 1)/(3-1) = (6561-1)/2 = 6560/2 = 3280",
    },
  },
};
